import { useEffect, useMemo, useState } from 'react'

import type { SessionResponse } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { relativeTime } from '@/lib/format'
import { StatusDot } from '@/ui/primitives'

interface Props {
  sessions: SessionResponse[]
  activeId: string | null
  onPickSession: (s: SessionResponse) => void
  onNewSession: () => void
  onSpike?: (s: SessionResponse) => void
  onPulp?: (s: SessionResponse) => void
  canSpike?: boolean
  canPulp?: boolean
  mobileOpen?: boolean
  onMobileClose?: () => void
}

function groupByHost(sessions: SessionResponse[]): [string, SessionResponse[]][] {
  const map = new Map<string, SessionResponse[]>()
  for (const s of sessions) {
    const key = s.target_server
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(s)
  }
  for (const arr of map.values()) {
    arr.sort(
      (a, b) =>
        new Date(b.last_used_at).getTime() - new Date(a.last_used_at).getTime(),
    )
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b))
}

function isRunning(s: SessionResponse): boolean {
  return s.turn_state === 'running'
}

function isBroken(s: SessionResponse): boolean {
  return s.turn_state === 'stalled' || s.status === 'broken'
}

function sessionNumber(s: SessionResponse, idx: number): string {
  const hex = s.session_id.replace(/[^0-9a-f]/gi, '').slice(-4).toUpperCase()
  if (hex) return hex.padStart(4, '0')
  return String(idx).padStart(4, '0')
}

function sessionHeadline(s: SessionResponse): string {
  if (s.title && s.title.trim()) return s.title
  return '(untitled)'
}

export function SessionList({
  sessions,
  activeId,
  onPickSession,
  onNewSession,
  onSpike,
  onPulp,
  canSpike = false,
  canPulp = false,
  mobileOpen = false,
  onMobileClose,
}: Props) {
  const { user } = useAuth()
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    if (!query.trim()) return sessions
    const q = query.toLowerCase()
    return sessions.filter(
      (s) =>
        s.target_server.toLowerCase().includes(q) ||
        s.title.toLowerCase().includes(q) ||
        s.session_id.toLowerCase().includes(q) ||
        s.owner_email.toLowerCase().includes(q),
    )
  }, [sessions, query])

  const grouped = groupByHost(filtered)

  useEffect(() => {
    if (!mobileOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onMobileClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [mobileOpen, onMobileClose])

  const handlePick = (s: SessionResponse) => {
    onPickSession(s)
    onMobileClose?.()
  }

  const handleNew = () => {
    onNewSession()
    onMobileClose?.()
  }

  const renderBody = () => {
    let idx = 0
    return (
      <>
        <div className="px-3 py-2 border-b border-line space-y-2">
          <div className="flex items-baseline justify-between">
            <div className="font-mono uppercase tracking-[0.08em] text-[12px] text-fg-mute">
              sessions
            </div>
            <span className="font-mono text-[11.5px] text-fg-dim tnum">
              [{sessions.length}]
            </span>
          </div>
          <div className="relative">
            <span
              aria-hidden
              className="absolute left-2 top-1/2 -translate-y-1/2 text-accent text-[13px] pointer-events-none"
            >
              /
            </span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="filter…"
              className="input pl-6"
            />
          </div>
          <button
            onClick={handleNew}
            className="btn btn-primary w-full justify-center"
          >
            <span className="text-fg-dim">[</span>
            <span>+</span>
            <span>new session</span>
            <span className="text-fg-dim">]</span>
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto py-2 font-mono text-[13px]">
          {grouped.length === 0 && (
            <div className="px-3 py-8 text-center text-fg-dim">
              <div className="text-[12px]">// no sessions</div>
              <div className="text-[12px] mt-1">start a new one to begin</div>
            </div>
          )}

          {grouped.map(([host, rows]) => (
            <div key={host} className="mb-3">
              <div className="px-3 pb-1 flex items-baseline justify-between">
                <span className="text-fg-soft">
                  <span className="text-fg-dim">▾</span> {host}
                </span>
                <span className="text-fg-dim text-[11.5px]">{rows.length}</span>
              </div>
              <ul>
                {rows.map((s, i) => {
                  const rowIdx = idx++
                  const isActive = s.session_id === activeId
                  const running = isRunning(s)
                  const broken = isBroken(s)
                  const num = sessionNumber(s, rowIdx)
                  const headline = sessionHeadline(s)
                  const isLast = i === rows.length - 1
                  return (
                    <li key={s.session_id} className="relative group">
                      <button
                        onClick={() => handlePick(s)}
                        className={`w-full text-left pl-3 pr-2 py-1.5 transition-colors ${
                          isActive
                            ? 'bg-surface text-fg'
                            : 'text-fg-soft hover:bg-surface hover:text-fg'
                        }`}
                        aria-label={`Open session ${num} — ${headline}`}
                      >
                        <div className="flex items-baseline gap-1.5 min-w-0">
                          <span className="text-fg-dim shrink-0">
                            {isLast ? '└─' : '├─'}
                          </span>
                          <span className={isActive ? 'text-accent' : 'text-fg-dim'}>
                            #{num}
                          </span>
                          <span className="truncate flex-1">{headline}</span>
                          {running && (
                            <StatusDot tone="accent" pulse size={7} className="shrink-0" />
                          )}
                          {broken && !running && (
                            <StatusDot tone="danger" size={7} className="shrink-0" />
                          )}
                          <span className="text-fg-dim text-[11.5px] shrink-0 tnum">
                            {relativeTime(s.last_used_at)}
                          </span>
                        </div>
                        {user?.role === 'admin' && s.owner_email && (
                          <div className="pl-6 mt-0.5 text-[11.5px] text-fg-dim truncate">
                            by {s.owner_email}
                          </div>
                        )}
                      </button>

                      {(onSpike || onPulp) && (
                        <div className="absolute right-1 top-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex gap-1 bg-bg-raise">
                          {canSpike && onSpike && running && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                onSpike(s)
                              }}
                              aria-label="Force stop this session"
                              className="btn btn-danger !h-5 !px-1 !text-[10.5px]"
                            >
                              <span className="text-fg-dim">[</span>stop<span className="text-fg-dim">]</span>
                            </button>
                          )}
                          {canPulp && onPulp && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                onPulp(s)
                              }}
                              aria-label="Delete this session"
                              className="btn !h-5 !px-1 !text-[10.5px]"
                            >
                              <span className="text-fg-dim">[</span>rm<span className="text-fg-dim">]</span>
                            </button>
                          )}
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>
      </>
    )
  }

  return (
    <>
      <div className="hidden min-[1200px]:block relative w-[40px] shrink-0">
        <aside className="group/sidebar absolute inset-y-0 left-0 w-[40px] hover:w-[300px] z-20 flex flex-col border-r border-line bg-bg-raise min-h-0 overflow-hidden transition-[width] duration-150">
          <div
            aria-hidden
            className="absolute inset-x-0 top-0 flex justify-center pt-3 pointer-events-none opacity-100 group-hover/sidebar:opacity-0 transition-opacity duration-150"
          >
            <span
              className="font-mono uppercase tracking-[0.15em] text-[10.5px] text-fg-mute"
              style={{ writingMode: 'vertical-rl' }}
            >
              sessions [{sessions.length}]
            </span>
          </div>

          <div className="w-[300px] flex flex-col flex-1 min-h-0 opacity-0 group-hover/sidebar:opacity-100 pointer-events-none group-hover/sidebar:pointer-events-auto transition-opacity duration-150">
            {renderBody()}
          </div>
        </aside>
      </div>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-50 min-[1200px]:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Sessions"
        >
          <div
            className="absolute inset-0 bg-bg/80 animate-fade-in"
            onClick={onMobileClose}
          />
          <aside className="absolute inset-y-0 left-0 w-[85vw] max-w-[320px] bg-bg-raise border-r border-line flex flex-col min-h-0 animate-slide-in-left">
            {renderBody()}
          </aside>
        </div>
      )}
    </>
  )
}
